import { assembleNameWithEmailFallback, User } from 'ComponentLibrary/src/common/users'
import { Stack } from '@mui/material'
import { Typography } from 'ComponentLibrary/src/piazza'
import { DateTimeString, isDateToday, isoDateToTimeIfTodayOrDate } from 'ComponentLibrary/src/common/date'
import { tokens } from 'ComponentLibrary/style-dictionary/output'

type ChangeLogItemData = {
    user?: Pick<User, 'email' | 'first_name' | 'last_name'>
    time: DateTimeString
}
type TaskChangeLogProps = {
    closed?: ChangeLogItemData
    created?: ChangeLogItemData
    copy?: Partial<TaskChangeLogCopy>
    locale?: string
}

export type TaskChangeLogCopy = {
    todayAt: (time: string) => string
    onDate: (date: string) => string
    createdBy: (userName: string, dateOrTime: string) => string
    createdAutomatically: (dateOrTime: string) => string
    closedBy: (userName: string, dateOrTime: string) => string
    closedAutomatically: (dateOrTime: string) => string
}

const DEFAULT_TASK_CHANGE_LOG_COPY: TaskChangeLogCopy = {
    todayAt: (time) => `heute um ${time} Uhr`,
    onDate: (date) => `am ${date}`,
    createdBy: (userName, dateOrTime) => `Aufgabe erstellt von ${userName} ${dateOrTime}`,
    createdAutomatically: (dateOrTime) => `Diese Aufgabe wurde automatisch erstellt ${dateOrTime}`,
    closedBy: (userName, dateOrTime) => `Aufgabe abgeschlossen von ${userName} ${dateOrTime}`,
    closedAutomatically: (dateOrTime) => `Diese Aufgabe wurde automatisch abgeschlossen ${dateOrTime}`,
}

const getDateOrTimeString = (dateTime: DateTimeString, copy: TaskChangeLogCopy, locale: string): string => {
    return isDateToday(dateTime)
        ? copy.todayAt(isoDateToTimeIfTodayOrDate(dateTime, locale))
        : copy.onDate(isoDateToTimeIfTodayOrDate(dateTime, locale))
}

const getTaskCreatedChangeLogItemText = (item: ChangeLogItemData, copy: TaskChangeLogCopy, locale: string) =>
    item.user
        ? copy.createdBy(assembleNameWithEmailFallback(item.user), getDateOrTimeString(item.time, copy, locale))
        : copy.createdAutomatically(getDateOrTimeString(item.time, copy, locale))

const getTaskClosedChangeLogItemText = (item: ChangeLogItemData, copy: TaskChangeLogCopy, locale: string) =>
    item.user
        ? copy.closedBy(assembleNameWithEmailFallback(item.user), getDateOrTimeString(item.time, copy, locale))
        : copy.closedAutomatically(getDateOrTimeString(item.time, copy, locale))

export function TaskChangeLog({ closed, created, copy, locale = 'de-DE' }: TaskChangeLogProps) {
    const resolvedCopy = { ...DEFAULT_TASK_CHANGE_LOG_COPY, ...copy }
    return (
        <Stack direction="column" gap={tokens.SizingSpacing075} padding={`${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`}>
            {closed && (
                <Typography color={tokens.ColorTextDefault} variant="textS">
                    {getTaskClosedChangeLogItemText(closed, resolvedCopy, locale)}
                </Typography>
            )}
            {created && (
                <Typography color={tokens.ColorTextDefault} variant="textS">
                    {getTaskCreatedChangeLogItemText(created, resolvedCopy, locale)}
                </Typography>
            )}
        </Stack>
    )
}
